/**
 * 처방 마감일 설정 API.
 *
 * 월별 마감일 당일까지 등록된 처방은 그 달, 다음날 0시부터는 다음 달 처방등록년월이 된다.
 * 설정하지 않은 달은 서버 기본 10일이 적용되며, 목록에는 설정한 달만 내려온다.
 */
import { isAxiosError } from 'axios';
import { apiClient } from '@/shared/api/http-client';
import { createApiPath } from '@/shared/api/endpoints';

export interface PrescriptionRegistrationDeadline {
  /** yyyyMM */
  yearMonth: string;
  /** yyyy-MM-dd */
  deadlineDate: string;
  /** 마감일 ≥ 오늘 */
  editable: boolean;
  /** 마감일 ≥ 오늘 && 기본 10일 ≥ 오늘 (지우면 이미 지난 10일 마감이 되는 경우 막음) */
  deletable: boolean;
  /** yyyy-MM-dd HH:mm:ss */
  updatedAt: string;
}

export interface CreatePrescriptionRegistrationDeadlineRequest {
  yearMonth: string;
  deadlineDate: string;
}

/** 서버 에러 코드 → 운영자 안내 문구 */
const DEADLINE_ERROR_MESSAGES: Record<string, string> = {
  PRESCRIPTION_DEADLINE_OUT_OF_MONTH: '마감일은 해당 월 안의 날짜여야 합니다.',
  PRESCRIPTION_DEADLINE_IN_PAST: '오늘 이후 날짜로만 지정할 수 있습니다.',
  PRESCRIPTION_DEADLINE_ALREADY_EXISTS:
    '이미 마감일이 설정된 월입니다. 목록을 새로고침한 뒤 수정해주세요.',
  PRESCRIPTION_DEADLINE_NOT_FOUND:
    '설정된 마감일이 없는 월입니다. 목록을 새로고침한 뒤 다시 추가해주세요.',
  PRESCRIPTION_DEADLINE_ALREADY_PASSED:
    '이미 마감이 지난 월이라 변경할 수 없습니다.',
};

/** 마감일 API 에러면 안내 문구, 아니면 null (호출부에서 handleApiError로 폴백) */
export const getPrescriptionDeadlineErrorMessage = (
  error: unknown,
): string | null => {
  if (!isAxiosError(error)) return null;
  const data = error.response?.data as { code?: unknown } | undefined;
  return typeof data?.code === 'string'
    ? (DEADLINE_ERROR_MESSAGES[data.code] ?? null)
    : null;
};

export const prescriptionRegistrationDeadlineService = {
  /** 연도별 설정 목록 (연월 오름차순, 설정한 달만) */
  list: async (year: number): Promise<PrescriptionRegistrationDeadline[]> => {
    const response = await apiClient.get(
      createApiPath.prescriptionRegistrationDeadlines.list(),
      { params: { year } },
    );
    return response.data ?? [];
  },

  /** 미설정 월에 마감일 추가 (201) */
  create: async (
    body: CreatePrescriptionRegistrationDeadlineRequest,
  ): Promise<void> => {
    await apiClient.post(
      createApiPath.prescriptionRegistrationDeadlines.list(),
      body,
    );
  },

  /** 설정된 월의 마감일 변경 (204) */
  update: async (yearMonth: string, deadlineDate: string): Promise<void> => {
    await apiClient.put(
      createApiPath.prescriptionRegistrationDeadlines.detail(yearMonth),
      { deadlineDate },
    );
  },

  /** 설정 삭제 → 기본 10일 복귀 (204, 설정 없어도 204) */
  remove: async (yearMonth: string): Promise<void> => {
    await apiClient.delete(
      createApiPath.prescriptionRegistrationDeadlines.detail(yearMonth),
    );
  },
};
